/**
 * Phase J-UX — Full-entry read view.
 *
 * Opens when the user clicks "Open" on an entry card. Shows the
 * full body, all metadata, mood tags, linked records, and any
 * attachments — without crowding the timeline card. Read-only;
 * actions live in the card's ••• menu.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  Lock,
  Eye,
  MapPin,
  CalendarDays,
  FileText,
  Paperclip,
  ArrowUpRight,
  Link2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MOOD_META } from "@/components/journal/MoodPicker";
import { API } from "@/lib/api";

const MOOD_BY_VALUE = Object.fromEntries(MOOD_META.map((m) => [m.value, m]));

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function JournalReadDialog({ open, onOpenChange, entry, childrenById }) {
  if (!entry) return null;
  const mood = MOOD_BY_VALUE[entry.mood];
  const MoodIcon = mood?.Icon;
  const linkedChildren = (entry.child_ids || [])
    .map((cid) => childrenById?.[cid])
    .filter(Boolean);

  const allLinks = [
    ...(entry.source_event_id
      ? [
          {
            type: "event",
            id: entry.source_event_id,
            label: entry.source_event_title || "Calendar event",
            href: `/app/calendar?spotlight=event-${entry.source_event_id}`,
          },
        ]
      : []),
    ...((entry.links || []).map((l) => ({ ...l, href: null }))),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-[#D9E1E3] max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        data-testid="journal-read-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-1.5 flex-wrap text-[10px] uppercase tracking-[0.12em] text-[#6F828A]">
            <span
              className={`px-2 py-0.5 border rounded-sm inline-flex items-center gap-1 ${
                entry.is_shared
                  ? "border-[#4A6170] bg-[#E6ECEF] text-[#2E3E48]"
                  : "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]"
              }`}
            >
              {entry.is_shared ? (
                <Eye className="w-3 h-3" strokeWidth={1.7} />
              ) : (
                <Lock className="w-3 h-3" strokeWidth={1.7} />
              )}
              {entry.is_shared ? "Shared" : "Private"}
            </span>
            <span>{fmtDateTime(entry.created_at)}</span>
            <span>·</span>
            <span>{entry.author_name}</span>
          </div>
          <DialogTitle className="font-heading text-xl mt-1 text-[#0B2E32]">
            {entry.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {mood ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span
                className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm inline-flex items-center gap-1"
                style={{ color: mood.color, background: mood.bg, borderColor: mood.color }}
              >
                {MoodIcon ? <MoodIcon className="w-3 h-3" strokeWidth={1.7} /> : null}
                {mood.label}
              </span>
              {(entry.mood_tags || []).map((t) => (
                <span
                  key={t}
                  className="text-[10px] tracking-[0.05em] px-2 py-0.5 border rounded-full bg-white border-[#D9E1E3] text-[#4A6170] capitalize"
                >
                  {t}
                </span>
              ))}
            </div>
          ) : null}

          <p
            className="text-sm text-[#2E3A3F] whitespace-pre-wrap leading-relaxed"
            data-testid="journal-read-body"
          >
            {entry.body}
          </p>

          {(linkedChildren.length > 0 || allLinks.length > 0 || entry.location) ? (
            <div className="border-t border-[#EEF3F4] pt-4 space-y-2">
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
                Linked
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {linkedChildren.map((c) => (
                  <span
                    key={c.id}
                    className="text-[11px] px-2 py-0.5 rounded-full border bg-white border-[#D9E1E3] text-[#3F535A] inline-flex items-center gap-1"
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: c.color || "#1F9FA4" }}
                    />
                    {c.name}
                  </span>
                ))}
                {entry.location ? (
                  <span className="text-[11px] px-2 py-0.5 rounded-full border bg-white border-[#D9E1E3] text-[#3F535A] inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3" strokeWidth={1.7} />
                    {entry.location}
                  </span>
                ) : null}
                {allLinks.map((lnk, idx) => {
                  const Icon =
                    lnk.type === "court_order"
                      ? Link2
                      : lnk.type === "event" || lnk.type === "contact_visit"
                      ? CalendarDays
                      : FileText;
                  const Chip = (
                    <span className="text-[11px] px-2 py-0.5 rounded-full border bg-[#E0F2F2] border-[#A7D8DA] text-[#116B6E] inline-flex items-center gap-1">
                      <Icon className="w-3 h-3" strokeWidth={1.7} />
                      {lnk.label || lnk.type}
                      {lnk.href ? <ArrowUpRight className="w-3 h-3 opacity-70" /> : null}
                    </span>
                  );
                  return lnk.href ? (
                    <Link key={`${lnk.type}-${idx}`} to={lnk.href}>
                      {Chip}
                    </Link>
                  ) : (
                    <React.Fragment key={`${lnk.type}-${idx}`}>{Chip}</React.Fragment>
                  );
                })}
              </div>
            </div>
          ) : null}

          {(entry.attachment_ids || []).length > 0 ? (
            <div className="border-t border-[#EEF3F4] pt-4 space-y-2">
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
                Attachments
              </div>
              <div className="flex flex-wrap gap-2">
                {entry.attachment_ids.map((fid) => (
                  <button
                    key={fid}
                    type="button"
                    onClick={() => window.open(`${API}/files/${fid}/download`, "_blank")}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-[#D9E1E3] bg-[#EEF3F4] rounded-sm text-xs text-[#3F535A] hover:bg-[#E8F0F1]"
                    data-testid={`journal-read-attachment-${fid}`}
                  >
                    <Paperclip className="w-3 h-3" /> Attachment
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default JournalReadDialog;
